import React, { useEffect, useState, type JSX } from 'react';
import {
    TextField,
    Stack,
    Typography,
    InputAdornment,
    IconButton,
    Button,
    useTheme,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import { Autocomplete } from '@react-google-maps/api';
import { LocationType, MapComponent } from '../../WidgetManagement/Widgets/map/Component';
import MyLocation from '@mui/icons-material/MyLocation';
import Search from '@mui/icons-material/Search';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setUpdateProperties } from '@fiji/common/src/features/deviceManagement/deviceSlice';
import { DECIMAL_REGEX } from '@fiji/common';
import { CustomTransComponent } from 'components';
import { setGoogleMapError } from '@fiji/common/src/features/common/commonSlice';

export const LocationUpdateModal = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const theme: any = useTheme();
    const [location, setLocation] = useState<LocationType>({
        lat: null,
        lng: null,
    });
    const [coordinates, setCoordinates] = useState<LocationType>({
        lat: null,
        lng: null,
    });
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [isAutocomplete, setIsAutocomplete] = useState(false);
    const [locationName, setLocationName] = useState('');
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const googleMapLoaded = useTypedSelector((state) => state.common.googleMapLoaded);
    const googleMapError = useTypedSelector((state) => state.common.googleMapError);
    const { propertiesPayload } = useTypedSelector((state) => state.device);

    useEffect(() => {
        if (propertiesPayload?.location) {
            setLocation(propertiesPayload?.location);
            setCoordinates(propertiesPayload?.location);
        }
    }, []);

    const locationChangeHandler = (e: any): void => {
        if (DECIMAL_REGEX.test(e.target.value) || !e?.target?.value) {
            setLocation((prev: any) => ({
                ...prev,
                [e?.target?.name]: e?.target?.value ? +e?.target?.value : null,
            }));
        }
    };

    const handleMapClick = (): void => {
        try {
            if (coordinates?.lat && coordinates?.lng) {
                const geocoder: any = new google.maps.Geocoder();
                geocoder
                    .geocode({ location: coordinates })
                    .then((response: any) => {
                        setLocationName(response.results[0]?.formatted_address ?? '');
                        setIsAutocomplete(true);
                    })
                    .catch(() => {});
            }
        } catch (error) {
            throw new Error('Error fetching location details');
        }
    };

    useEffect(() => {
        if (coordinates?.lat && !isAutocomplete) handleMapClick();
    }, [coordinates, isAutocomplete]);

    useEffect(() => {
        dispatch(
            setUpdateProperties({
                key: 'location',
                value: {
                    lat: coordinates?.lat ? +coordinates?.lat : null,
                    lng: coordinates?.lng ? +coordinates?.lng : null,
                },
            })
        );
    }, [coordinates]);

    const onPlaceSelect = (): void => {
        setLocationName(autocomplete?.getPlace()?.formatted_address);
        const lat = parseFloat(autocomplete?.getPlace()?.geometry?.location.lat()) ?? 0;
        const lng = parseFloat(autocomplete?.getPlace()?.geometry.location.lng()) ?? 0;
        setCoordinates({ lat, lng });
        setLocation({ lat, lng });
    };
    const mapErrorHandler = (err: any): void => {
        dispatch(setGoogleMapError(err));
    };

    React.useEffect(() => (): any => mapErrorHandler(undefined), []);

    const getLocation = (): void => {
        if (!navigator.geolocation) {
            mapErrorHandler('Geolocation is not supported by this browser.');
            return;
        }

        setIsLoadingLocation(true);
        mapErrorHandler(undefined);

        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setIsLoadingLocation(false);
                mapErrorHandler(undefined);
                setCoordinates({ lat, lng });
                setIsAutocomplete(false);
                setLocation({ lat, lng });
            },
            (error: GeolocationPositionError) => {
                setIsLoadingLocation(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        mapErrorHandler('Location access denied. Please enable location permissions in your browser.');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        mapErrorHandler('Location information is unavailable.');
                        break;
                    case error.TIMEOUT:
                        mapErrorHandler('Location request timed out. Please try again.');
                        break;
                    default:
                        mapErrorHandler('GeolocationPositionError');
                        break;
                }
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    return (
        <>
            <Typography variant="body1">
                <CustomTransComponent translationKey={'COMMON:ENTER_LOCATION'} />
            </Typography>
            <Stack
                direction={'row'}
                pb={2}
                my={2}
                width={'100%'}
                justifyContent={'space-between'}
                className="border-bottom-1"
            >
                {googleMapLoaded && (
                    <Autocomplete
                        className="w-100"
                        onLoad={(autocompleteData: any): void => {
                            setAutocomplete(autocompleteData);
                        }}
                        onPlaceChanged={onPlaceSelect}
                    >
                        <Stack direction={'row'}>
                            {locationName ? (
                                <TextField
                                    data-cy="location"
                                    onChange={(event): void => {
                                        setLocationName(event.target.value);
                                        setIsAutocomplete(true);
                                    }}
                                    error={Boolean(googleMapError)}
                                    value={locationName ?? ''}
                                    label="Location"
                                    sx={{
                                        '& .Mui-focused': {
                                            color: '#424e54',
                                        },
                                        '& .Mui-focused:after': {
                                            borderBottom: 'none',
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    id="my-location"
                                                    aria-label="location"
                                                    onClick={getLocation}
                                                    disabled={isLoadingLocation}
                                                >
                                                    {isLoadingLocation ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <MyLocation id="myLocation" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    variant="filled"
                                    fullWidth
                                    focused
                                />
                            ) : (
                                <TextField
                                    data-cy="location"
                                    label={<CustomTransComponent translationKey={'COMMON:LOCATION'} />}
                                    variant="filled"
                                    fullWidth
                                    error={Boolean(googleMapError)}
                                    onChange={(): void => {
                                        setIsAutocomplete(true);
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    id="my-location"
                                                    aria-label="location"
                                                    onClick={getLocation}
                                                    disabled={isLoadingLocation}
                                                >
                                                    {isLoadingLocation ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <MyLocation />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            {Boolean(googleMapError) && (
                                <FormHelperText error={Boolean(googleMapError)}>
                                    {googleMapError !== 'GeolocationPositionError' ? (
                                        googleMapError
                                    ) : (
                                        <CustomTransComponent translationKey={'COMMON:MAP_LOCATION_ERROR'} />
                                    )}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Autocomplete>
                )}
            </Stack>
            <Stack direction={'row'} spacing={2} width={'100%'}>
                <Stack justifyContent={'space-between'} width={'50%'}>
                    <Stack spacing={2}>
                        <TextField
                            id="lat"
                            label={<CustomTransComponent translationKey={'COMMON:LAT'} />}
                            name="lat"
                            variant="filled"
                            value={location?.lat ?? ''}
                            onChange={locationChangeHandler}
                            fullWidth
                        />
                        <TextField
                            id="lng"
                            label={<CustomTransComponent translationKey={'COMMON:LNG'} />}
                            name="lng"
                            variant="filled"
                            value={location?.lng ?? ''}
                            onChange={locationChangeHandler}
                            fullWidth
                        />
                    </Stack>
                    <Button
                        id="search"
                        variant="outlined"
                        onClick={(): void => {
                            setCoordinates(location);
                            setIsAutocomplete(false);
                        }}
                        disabled={!location?.lat || !location?.lng}
                        sx={{
                            border: `1px solid ${theme?.palette?.primary?.main}`,
                            '&:hover': {
                                backgroundColor: theme?.palette?.primary?.[50],
                            },
                        }}
                        startIcon={<Search />}
                    >
                        <CustomTransComponent translationKey={'COMMON:SEARCH_PLACEHOLDER'} />
                    </Button>
                </Stack>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    height={'232px'}
                    width={'365px'}
                >
                    {googleMapLoaded && (
                        <MapComponent
                            widgetData={{ secondary: [{ coordinates }] }}
                            clustering={false}
                            showInfoWindow={false}
                            markerSelector
                            center={coordinates?.lat && coordinates?.lng ? coordinates : undefined}
                            onLocationFetch={(lat: number, lng: number): void => {
                                setCoordinates({ lat, lng });
                                setIsAutocomplete(false);
                                setLocation({ lat, lng });
                            }}
                        />
                    )}
                </Stack>
            </Stack>
        </>
    );
};
